import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TimerService } from '../../../../shared/service/timer.service';
import { LoginService } from '../service/login.service';
import { Router } from '@angular/router';
import { TokenService } from '../../../../shared/service/token.service';
import { AuthService } from '../../../../core/services/auth.service';
import { Subject,  takeUntil } from 'rxjs';
import { CustomToastComponent } from 'src/app/shared/component/custom-toast/custom-toast.component';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { UserOrgService } from 'src/app/shared/service/user-org.service';
import { SharedDataService } from 'src/app/shared/service/shared-data/shared-data.service';
import { UserLocationService } from 'src/app/shared/service/user-location.service';
import { DepartmentService } from '../../location/services/department.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { OrganizationService } from '../../location/services/organization.service';
import { EnvironmentService } from 'src/app/shared/environment.service';

@Component({
  selector: 'app-login-otp',
  templateUrl: './login-otp.component.html',
  styleUrl: './login-otp.component.scss',
})
export class LoginOtpComponent implements OnInit, OnDestroy,OnChanges {
  @Output() updateLoginStatus = new EventEmitter<any>();
  @Output() closeLogin = new EventEmitter<any>();
  @Input('frgtPwdOtp') frgtPwdOtp: boolean = false;
  @Input() disableOtp!: boolean;
  @Output() updateOtpAttempt = new EventEmitter<any>();

  config = { length: 5, disableAutoFocus: false, placeholder: '' };

  // OTP validity (5 min) display
  otpMinutes = 5;
  otpSeconds = 0;
  otpExpired = false;

  // Cooldown (resend / regenerate) – separate from OTP validity
  cooldownMinutes = 0;
  cooldownSeconds = 0;
  private cooldownTimer: any;

  // Controls
  disableResendOtp = true;   // locked for first 60s
  resendAttempts = 0;        // after 3 → switch to regenerate
  showRegenerate = false;
  disableRegenerate = false;

  // OTP value
  otpValue: any;

  private destroy$ = new Subject<void>();

  constructor(
    public timerService: TimerService,
    public loginService: LoginService,
    private router: Router,
    public tokenService: TokenService,
    public _auth: AuthService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private userOrgService: UserOrgService,
    private sharedDataService: SharedDataService,
    private _locationService: UserLocationService,
    private _departmentService: DepartmentService,
    private permissionsService: PermissionsService,
    private orgService: OrganizationService,
    private envService: EnvironmentService
  ) {}

  ngOnInit() {
    // Start global 5-minute validity ONE time at screen open
    this.startOtpValidityTimer();

    // Lock “Resend” for 1 minute after first OTP was sent
    this.disableResendOtp = true;
    this.startCooldown(60, false);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disableOtp']) {
      // this.disableResendOtp = this.disableOtp;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timerService.stopTimer();
    clearInterval(this.cooldownTimer);
  }

  // -------------------------------
  // OTP VALIDITY (5 minutes global timer)
  // -------------------------------
  private startOtpValidityTimer() {
    this.otpExpired = false;  // reset
    this.timerService.startTimer(5 * 60);

    let firstEmission = true;  //  track first tick

    this.timerService.timeRemaining$
      .pipe(takeUntil(this.destroy$))
      .subscribe((time) => {
        // Ignore first emission if it's full duration
        if (firstEmission) {
          firstEmission = false;
          this.otpMinutes = Math.floor(time / 60);
          this.otpSeconds = time % 60;
          return;
        }

        this.otpMinutes = Math.floor(time / 60);
        this.otpSeconds = time % 60;

        if (time <= 0) {
          this.otpMinutes = 0;
          this.otpSeconds = 0;
          this.otpExpired = true;
          this.showRegenerate = true;
          this.disableRegenerate = false;
        }
    });
  }

  // -------------------------------
  // RESEND / REGENERATE LOGIC
  // -------------------------------
  resendCode() {
    if (!this.showRegenerate) {
      // ---------- RESEND FLOW ----------
      if (this.resendAttempts < 3 && !this.disableResendOtp) {
        this.loginService.requestOtp(this.loginService.email).subscribe((otpData) => {
          if (otpData.status === 200) {
            this.translate.get('login_otp.message.otp_sent').subscribe((msg: string) => {
              this.showSnackbar(msg, 3000);
            });

            this.resendAttempts++;

            // Lock resend for 1 minute (but do not reset global 5 min timer)
            this.disableResendOtp = true;
            this.startCooldown(60, false);

            // After 3 resends, just disable — wait for global expiry
            if (this.resendAttempts >= 3) {
              this.disableResendOtp = true;
            }
          }
        });
      }
    } else {
      // ---------- REGENERATE FLOW ----------
      if (!this.disableRegenerate) {
        this.loginService.requestOtp(this.loginService.email).subscribe((otpData) => {
          if (otpData.status === 200) {
            this.translate.get('login_otp.message.otp_sent').subscribe((msg: string) => {
              this.showSnackbar(msg, 3000);
            });

            // Reset everything
            this.showRegenerate = false;
            this.resendAttempts = 0;

            // Restart 5-minute validity timer
            this.startOtpValidityTimer();

            // Lock resend for 1 minute
            this.disableResendOtp = true;
            this.startCooldown(60, false);
          }
        });
      }
    }

    this.updateOtpAttempt.emit(true);
  }


  // -------------------------------
  // COOLDOWN HANDLER (independent of OTP validity)
  // -------------------------------
  private startCooldown(duration: number, isRegenerate: boolean) {
    clearInterval(this.cooldownTimer);
    let remaining = duration;

    this.cooldownMinutes = Math.floor(remaining / 60);
    this.cooldownSeconds = remaining % 60;

    this.cooldownTimer = setInterval(() => {
      if (remaining > 0) {
        remaining--;
        this.cooldownMinutes = Math.floor(remaining / 60);
        this.cooldownSeconds = remaining % 60;
      } else {
        clearInterval(this.cooldownTimer);
        if (isRegenerate) {
          this.disableRegenerate = false;   // enable regenerate
        } else if(this.resendAttempts < 3){
          this.disableResendOtp = false;    // enable resend
        }
      }
    }, 1000);
  }

  onOtpChange(event: any) {
    this.otpValue = event;
  }

  // -------------------------------
  // SNACKBAR
  // -------------------------------
  private showSnackbar(message: string, duration: number) {
    const snackBarMsg = message + this.loginService.email;
    const snackbarOptions: any = {
      data: { message: snackBarMsg, icon: 'checked', action: 'close', color: 'green' },
      panelClass: 'snackbar',
      duration,
    };
    this.snackbarService.openFromComponent(CustomToastComponent, snackbarOptions);
  }

  // -------------------------------
  // SUBMIT OTP
  // -------------------------------
  submitOtp() {
    const apiData = { email: this.loginService.email, otp: this.otpValue };
    this.disableResendOtp = true;

    this.loginService.verifyOtp(apiData).subscribe((otpData) => {
      if (otpData.status === 200) {
        this.updateOtpAttempt.emit(false);

        if (!this.frgtPwdOtp) {
          if (otpData?.body) {
            this._locationService.setLocationData(otpData?.body?.locationIds);
            this._locationService.setSelLocationName(otpData?.body?.locationIds[0].locationName);
            this._departmentService.setbaseDeptId(otpData?.body?.baseDepartmentId);
            this.sharedDataService.organizationId = otpData?.body?.organisationId;
            this.userOrgService.setOrgId(otpData?.body?.organisationId);
            this.userOrgService.setUserId(otpData?.body?.staffId);
            if (otpData?.body?.oneSignalUserId) {
              this.userOrgService.setOneSignalUserId(otpData.body.oneSignalUserId);
            }
            this.sharedDataService.locationId = otpData?.body?.locationIds[0].locationId;
            this.sharedDataService.setPlanFormSubmitted(true);
          }
          this.tokenService.saveToken(otpData.body.jwt);
          this.tokenService.saveRefreshToken(otpData.body.refreshToken);
          this.tokenService.saveStemplingEnabled(otpData.body.isStemplingEnabled);
          this._auth.refreshTokenInterval(
            otpData.body.expiryDate - Math.floor(Date.now())
          );
          if (otpData?.body?.locationIds?.length > 0) {
            const baseLocation = otpData?.body?.locationIds.find((loc: any) => loc.baseLocation === true);
            const baseLocationId = baseLocation ? baseLocation.locationId : otpData?.body?.locationIds[0].locationId;
            this.permissionsService.loadUserRoleAndPermissions(otpData?.body?.userRole, baseLocationId).subscribe();
          }
          this.closeLogin.emit(true);
          this.router.navigateByUrl('/dashboard');
          this.getOrgData();
        } else {
          this.updateLoginStatus.emit({ type: 'change-pwd', status: true });
        }
      }
    });
  }

  getOrgData() {
    this.orgService.fetchOrganization().subscribe({
      next: (orgData: any) => {
        if (orgData.status === 200) {
          this.sharedDataService.setOrgDetails(orgData.body);
          const logoUrl = orgData.body.logo_url
            ? `${this.envService.imgReviewUrl}/${orgData.body.logo_url}`
            : 'assets/image/no-org-logo.webp';

          this.sharedDataService.setOrgLogo(logoUrl);
        }
      },
    });
  }

  // -------------------------------
  // BACK TO LOGIN / FORGOT PWD
  // -------------------------------
  showLogin(type: string) {
    this.disableResendOtp = true;
    if (type === 'login' || (!this.frgtPwdOtp && type === 'back')) {
      this.updateLoginStatus.emit({ type: 'login', status: true });
    } else if (type === 'back') {
      this.updateLoginStatus.emit({ type: 'forgot-pwd', status: true });
    }
  }
}
